import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Wallet, CheckCircle2, Tag, Loader2, LogIn, User as UserIcon, Mail, KeyRound, Info, TrendingUp } from "lucide-react";
import { useAuth, API } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { UnavailableAlert } from "../../components/CartOverlay";
import { usePaymentCharges } from "../../hooks/usePaymentCharges";

// iter201: mirrors backend `_voucher_value` so the summary matches what checkout charges.
const computeVoucherValue = (v, total) => {
  if (v.min_cart_value > 0 && total + 0.005 < v.min_cart_value) return 0;
  let d;
  if (v.discount_type === "percent") {
    d = (total * (v.percent || 0)) / 100;
    if (v.max_discount > 0) d = Math.min(d, v.max_discount);
  } else if (v.is_discount) {
    d = v.amount || 0;
  } else {
    d = v.balance || 0;
  }
  return Math.round(Math.min(d, total) * 100) / 100;
};

const voucherSummary = (v) => {
  if (v.discount_type === "percent") return `${v.percent}% off${v.max_discount > 0 ? ` (max €${v.max_discount.toFixed(2)})` : ""}`;
  if (v.is_discount) return `€${(v.amount || 0).toFixed(2)} off`;
  return `Balance €${(v.balance || 0).toFixed(2)}`;
};

export default function Checkout() {
  const [eventsById, setEventsById] = useState({}); // event_id → event payload (for names/packages)
  const [wallet, setWallet] = useState(null);
  const [preview, setPreview] = useState(null);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [useWallet, setUseWallet] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(null);
  const [pollingSession, setPollingSession] = useState(null);

  // Guest checkout state
  const [guestToken, setGuestToken] = useState(() => sessionStorage.getItem("guest_checkout_token") || null);
  const [guestInfo, setGuestInfo] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("guest_checkout_info") || "null"); } catch { return null; }
  });
  const [guestStage, setGuestStage] = useState(guestToken ? "verified" : "start"); // start | otp_sent | existing_user | verified
  const [guestName, setGuestName] = useState(guestInfo?.name || "");
  const [guestEmail, setGuestEmail] = useState(guestInfo?.email || "");
  const [guestOtp, setGuestOtp] = useState("");
  const [guestPassword, setGuestPassword] = useState("");
  const [guestExistingMessage, setGuestExistingMessage] = useState("");
  const [guestSending, setGuestSending] = useState(false);
  const [guestVerifying, setGuestVerifying] = useState(false);

  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [search] = useSearchParams();
  const location = useLocation();
  const { user, authAxios, token: userToken, login: authLogin } = useAuth();
  const { items: cartItems, packages: cartPackages, groupedByEvent, clear: clearCart, count: cartCount, setTicketQty, setPackageQty, removeItem, removePackage, unavailable = {} } = useCart();
  const unavailableLines = [...cartItems.map(i => ({ id: i.id, name: `${i.ticket_type_name}${i.variant_name ? ` · ${i.variant_name}` : ""}`, remove: () => removeItem(i.id) })), ...cartPackages.map(p => ({ id: p.id, name: p.package_name, remove: () => removePackage(p.id) }))].filter(l => unavailable[l.id]);
  const pollIntervalRef = useRef(null);

  // Anyone who can proceed: real logged user OR an OTP-verified guest.
  const canCheckout = !!user || !!guestToken;
  const buyerEmail = user?.email || guestInfo?.email || "";
  const isGuestOtp = !user && !!guestToken;

  // HTTP client honouring whichever token is active this moment.
  const httpClient = useMemo(() => {
    const bearer = userToken || guestToken;
    return axios.create({
      baseURL: API,
      headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
    });
  }, [userToken, guestToken]);

  // Assemble cart payload the backend understands.
  const cartPayload = useMemo(() => ({
    items: cartItems.map(i => ({
      ticket_type_id: i.ticket_type_id,
      quantity: i.quantity,
      variant_id: i.variant_id || null,
    })),
    packages: cartPackages.map(p => ({
      package_id: p.package_id,
      quantity: p.quantity,
      variant_selections: p.variant_selections || undefined,
    })),
  }), [cartItems, cartPackages]);

  // Detect Stripe redirect polling first — separate from cart resolution.
  useEffect(() => {
    const ticketSession = search.get("ticket_session");
    if (ticketSession) setPollingSession(ticketSession);
  }, [search]);

  // Fetch events referenced by cart items (multi-event supported) + wallet + backend preview.
  useEffect(() => {
    if (pollingSession) return;
    if (cartCount === 0) return;
    const eventIds = Array.from(new Set([
      ...cartItems.map(i => i.event_id).filter(Boolean),
      ...cartPackages.map(p => p.event_id).filter(Boolean),
    ]));
    Promise.all(
      eventIds.map(eid =>
        axios.get(`${API}/ticketing/public/events/${eid}`).then(r => [eid, r.data]).catch(() => [eid, null])
      )
    ).then(pairs => {
      const map = {};
      for (const [eid, ev] of pairs) if (ev) map[eid] = ev;
      setEventsById(map);
    });
    if (user) {
      authAxios.get("/wallet/my-balance").then(r => setWallet(r.data)).catch(() => setWallet({ balance: 0 }));
    } else {
      setWallet({ balance: 0 });
    }
  }, [cartCount, pollingSession, user]);

  // Self-healing: refresh cart entries' base_unit_price against the live event data.
  // Users can leave items in localStorage across price/config changes; this ensures
  // the checkout page never shows a stale price the shop no longer offers.
  useEffect(() => {
    if (pollingSession) return;
    if (Object.keys(eventsById).length === 0) return;
    for (const it of cartItems) {
      const ev = eventsById[it.event_id];
      if (!ev) continue;
      const t = ev.ticket_types?.find(x => x.id === it.ticket_type_id);
      if (!t) continue;
      let fresh;
      if (it.variant_id) {
        const v = (t.variants || []).find(x => x.id === it.variant_id);
        const raw = v?.price_override != null ? Number(v.price_override) : Number(t.price || 0);
        fresh = raw;
        if (t.discount && t.discount.value > 0) {
          if (t.discount.type === "percent") fresh = Math.max(0, Math.round(raw * (1 - t.discount.value / 100) * 100) / 100);
          else if (t.discount.type === "fixed") fresh = Math.max(0, Math.round((raw - t.discount.value) * 100) / 100);
        }
      } else {
        fresh = Number(t.effective_price ?? t.price ?? 0);
      }
      if (Math.abs(fresh - Number(it.base_unit_price || 0)) > 0.005) {
        setTicketQty({ ...it }, it.quantity); // triggers re-write; base_unit_price stays as-is until we pass fresh
        // Direct update via setTicketQty with new base:
        setTicketQty({ ...it, base_unit_price: fresh }, it.quantity);
      }
    }
    for (const p of cartPackages) {
      const ev = eventsById[p.event_id];
      if (!ev) continue;
      const pkg = ev.packages?.find(x => x.id === p.package_id);
      if (!pkg) continue;
      const fresh = Number(pkg.effective_price ?? pkg.base_price ?? 0);
      if (Math.abs(fresh - Number(p.base_unit_price || 0)) > 0.005) {
        setPackageQty({ ...p, base_unit_price: fresh }, p.quantity);
      }
    }
    // Only run when eventsById changes; ignoring cart array refs to avoid loop.
  }, [eventsById]);

  // Preview needs authentication (real user OR guest_otp token).
  useEffect(() => {
    if (pollingSession) return;
    if (cartCount === 0) return;
    if (!canCheckout) { setPreview(null); return; }
    httpClient.post("/ticketing/checkout/preview", cartPayload)
      .then(r => setPreview(r.data))
      .catch(() => setPreview(null));
  }, [cartCount, pollingSession, canCheckout, httpClient, cartPayload]);

  // Stripe success polling
  useEffect(() => {
    if (!pollingSession) return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const r = await httpClient.get(`/ticketing/payment-status/${pollingSession}`);
        if (r.data.status === "complete") {
          clearInterval(pollIntervalRef.current);
          setSuccess({
            tickets: r.data.tickets || [],
            order_id: r.data.order_id,
            amount_paid: r.data.amount_paid || 0,
            wallet_portion: r.data.wallet_portion || 0,
            voucher_portion: r.data.voucher_portion || 0,
          });
          clearCart();
          sessionStorage.removeItem("guest_checkout_token");
          sessionStorage.removeItem("guest_checkout_info");
        } else if (r.data.status === "expired") {
          clearInterval(pollIntervalRef.current);
          toast.error("Payment session expired");
          navigate(`/${tenantSlug}`);
        }
      } catch (e) { /* keep polling */ }
      if (attempts > 30) {
        clearInterval(pollIntervalRef.current);
        if (user) {
          toast.error("Payment confirmation timed out — check My Tickets shortly");
          navigate(`/${tenantSlug}/my-tickets`);
        } else {
          toast.error("Payment confirmation timed out — please check your email for the ticket confirmation");
          navigate(`/${tenantSlug}`);
        }
      }
    };
    pollIntervalRef.current = setInterval(poll, 2000);
    poll();
    return () => clearInterval(pollIntervalRef.current);
  }, [pollingSession]);

  // Empty-cart guard — kick back to the shop, unless we're processing a Stripe return.
  useEffect(() => {
    if (pollingSession) return;
    if (cartCount === 0 && !success) navigate(`/${tenantSlug}`);
  }, [cartCount, pollingSession, success, navigate, tenantSlug]);

  const _redirectToLogin = () => {
    const backTo = location.pathname + location.search;
    // Prefer sending them back to the event they were shopping if a single one is in the cart;
    // otherwise back to checkout (they can review from the cart icon after login).
    const singleEvent = Object.keys(eventsById);
    const returnTo = singleEvent.length === 1 ? `/${tenantSlug}/event/${singleEvent[0]}` : backTo;
    navigate(`/${tenantSlug}/login?next=${encodeURIComponent(returnTo)}`);
  };

  const sendGuestOtp = async () => {
    if (!guestName.trim() || guestName.trim().length < 2) return toast.error("Please enter your name");
    if (!/^\S+@\S+\.\S+$/.test(guestEmail)) return toast.error("Please enter a valid email");
    setGuestSending(true);
    try {
      const r = await axios.post(`${API}/ticketing/guest/send-otp`, { email: guestEmail.trim(), name: guestName.trim() });
      if (r.data?.status === "existing_user") {
        // Registered user — prompt password login instead of OTP.
        setGuestStage("existing_user");
        setGuestExistingMessage(r.data.message || "This email is already registered. Please log in with your password.");
        if (r.data.name) setGuestName(r.data.name);
      } else {
        setGuestStage("otp_sent");
        toast.success("Verification code sent — check your inbox");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send code");
    } finally { setGuestSending(false); }
  };
  const loginExistingUser = async () => {
    if (!guestPassword) return toast.error("Enter your password");
    setGuestVerifying(true);
    try {
      const r = await axios.post(`${API}/wallet/login`, { email: guestEmail.trim(), password: guestPassword });
      // Reuse the AuthContext login (persists token in localStorage, refreshes header).
      authLogin(r.data.token, {
        name: r.data.name,
        email: guestEmail.trim(),
        customer_id: r.data.customer_id,
        role: r.data.role || "member",
        must_change_password: false,
      });
      toast.success(`Welcome back, ${r.data.name || "friend"}!`);
      // Do NOT navigate away — user stays on checkout with their cart intact.
    } catch (e) {
      toast.error(e.response?.data?.detail || "Incorrect password");
    } finally { setGuestVerifying(false); }
  };
  const verifyGuestOtp = async () => {
    if (!guestOtp.trim() || guestOtp.trim().length < 4) return toast.error("Enter the code from your email");
    setGuestVerifying(true);
    try {
      const r = await axios.post(`${API}/ticketing/guest/verify-otp`, { email: guestEmail.trim(), otp: guestOtp.trim() });
      const info = { email: r.data.email, name: r.data.name };
      setGuestToken(r.data.token);
      setGuestInfo(info);
      setGuestStage("verified");
      sessionStorage.setItem("guest_checkout_token", r.data.token);
      sessionStorage.setItem("guest_checkout_info", JSON.stringify(info));
      toast.success("Email verified — you can complete checkout as a guest");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally { setGuestVerifying(false); }
  };

  // === Order totals ===
  // Pre-auth (no preview yet): totals are computed from cart's base_unit_price which reflects
  // the post-tier-discount effective price the shopper saw when adding items.
  // Once auth'd (user OR guest_otp), preview.total is authoritative and package/tier discounts
  // + member-price rules are applied on the backend.
  const cartTotalFromLocal =
    cartItems.reduce((s, i) => s + Number(i.base_unit_price || 0) * i.quantity, 0) +
    cartPackages.reduce((s, p) => s + Number(p.base_unit_price || 0) * p.quantity, 0);
  const total = Number((preview ? preview.total : cartTotalFromLocal).toFixed(2));
  const walletBal = wallet?.balance || 0;
  const voucherPortion = voucher ? computeVoucherValue(voucher, total) : 0;
  const remainingAfterV = Math.max(0, total - voucherPortion);
  const walletPortion = useWallet && !isGuestOtp ? Math.min(walletBal, remainingAfterV) : 0;
  const stripePortion = Math.max(0, total - voucherPortion - walletPortion);
  // Processing fee applies only to what actually hits Stripe. Wallet/voucher
  // portions are already internal balances so they carry no processing fee.
  // Audience: OTP-only guests are unregistered; otherwise use the logged-in
  // user's role. Passed to the fee hook so FA overrides per audience apply.
  const paymentAudience = isGuestOtp ? "unregistered" : (user?.role || "unregistered");
  const { computeFee: computeStripeFee, label: chargesLabel } = usePaymentCharges(paymentAudience);
  const stripeFee = computeStripeFee(stripePortion);
  const youPay = Math.round((stripePortion + stripeFee) * 100) / 100;

  // === Tier upsell — representative savings ===
  // Actual savings depend on FA-configured tier/member-price rules per item,
  // family quotas, and vouchers. We surface a *representative* figure here so
  // shoppers understand the value of upgrading tier without over-promising.
  // Rates are conservative averages of typical member/guest discounts across
  // Maitree tenants.
  //   Anonymous / OTP-guest → ~15% by moving up to registered guest/member.
  //   Registered guest       → ~10% additional by becoming a full member.
  const _SAVINGS_PCT_TO_MEMBER = 0.15;   // for anonymous / guest_otp
  const _SAVINGS_PCT_GUEST_TO_MEMBER = 0.10; // for registered guest
  let upsellTier = null;
  if (!canCheckout) upsellTier = "anon";
  else if (isGuestOtp) upsellTier = "guest_otp";
  else if (user?.role === "guest") upsellTier = "guest";
  const _upsellPct =
    upsellTier === "guest" ? _SAVINGS_PCT_GUEST_TO_MEMBER : _SAVINGS_PCT_TO_MEMBER;
  const upsellSavings = upsellTier
    ? Math.round(Math.max(0, total) * _upsellPct * 100) / 100
    : 0;
  const upsellCopy = (() => {
    if (!upsellTier || upsellSavings <= 0) return null;
    if (upsellTier === "anon")
      return {
        title: `Save up to ~€${upsellSavings.toFixed(2)} on this order`,
        body: "Sign in — members and registered guests unlock discounted pricing on many tickets and packages.",
      };
    if (upsellTier === "guest_otp")
      return {
        title: `Save up to ~€${upsellSavings.toFixed(2)} on this order`,
        body: "You're an unregistered guest right now — log in with a member or registered-guest account to unlock discounted tier pricing.",
      };
    // registered guest
    return {
      title: `Save up to ~€${upsellSavings.toFixed(2)} more on this order`,
      body: "Become a full member to unlock member-only pricing on many tickets and packages.",
    };
  })();

  const applyVoucher = async () => {
    setVoucherLoading(true);
    try {
      // Use the auth'd /voucher/validate when a bearer is available (enforces email
      // restriction), else fall back to the public /voucher/preview endpoint so
      // pre-auth shoppers can still see the voucher code is valid.
      const endpoint = canCheckout ? "/ticketing/voucher/validate" : "/ticketing/voucher/preview";
      const r = await httpClient.post(endpoint, { code: voucherCode, cart_total: total, items: cartPayload.items, packages: cartPayload.packages });
      setVoucher(r.data);
      const hint = r.data.requires_email_match ? " (requires email match)" : "";
      toast.success(`Voucher applied: ${voucherSummary(r.data)}${hint}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid voucher"); setVoucher(null); }
    finally { setVoucherLoading(false); }
  };

  const [stalePreview, setStalePreview] = useState(null); // {message, preview}
  const handlePay = async () => {
    setProcessing(true);
    // A per-click idempotency key so a rapidly-double-clicked Pay button or
    // parallel-tab submission is rejected by the backend as DUPLICATE_SUBMISSION.
    const idemKey = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const expectedTotal = Number(preview?.total ?? total).toFixed(2);
    try {
      const origin = window.location.origin + "/" + (window.location.pathname.split("/")[1] || tenantSlug || "mymaitree");
      const res = await httpClient.post("/ticketing/checkout", {
        items: cartPayload.items,
        packages: cartPayload.packages,
        voucher_code: voucher?.code,
        use_wallet: useWallet && !isGuestOtp,
        origin_url: origin,
        expected_total: Number(expectedTotal),
        idempotency_key: idemKey,
      });
      if (res.data.next_action === "stripe_redirect" && res.data.checkout_url) {
        sessionStorage.setItem("ticketing_pending", JSON.stringify({ session: res.data.session_id, total }));
        // Use replace() so Stripe's hosted checkout takes Checkout's slot in
        // browser history. This prevents a Back-button loop between Checkout
        // and Stripe's multi-step flow and lets the user return to Shop with a
        // single Back press from Stripe.
        window.location.replace(res.data.checkout_url);
      } else {
        clearCart();
        sessionStorage.removeItem("guest_checkout_token");
        sessionStorage.removeItem("guest_checkout_info");
        setSuccess(res.data);
        toast.success(`${res.data.tickets.length} ticket(s) confirmed!`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Stale cart or duplicate submission → surface a modal so the user can
      // review the fresh preview before submitting again.
      if (e.response?.status === 409 && detail && typeof detail === "object") {
        if (detail.code === "STALE_CART" && detail.preview) {
          setPreview(detail.preview);
          setStalePreview({ message: detail.message || "Prices changed — please review.", preview: detail.preview });
        } else if (detail.code === "DUPLICATE_SUBMISSION") {
          setStalePreview({ message: detail.message || "This order is already being processed.", preview: null });
        } else {
          toast.error(detail.message || "Checkout failed");
        }
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Checkout failed"));
      }
    } finally { setProcessing(false); }
  };

  if (pollingSession && !success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="checkout-polling">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#1e3a5f] mx-auto mb-3" />
          <p className="text-slate-600">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="checkout-success">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-lg">
          <CheckCircle2 className="w-16 h-16 text-[#2d8a4e] mx-auto mb-4" />
          <h2 className="text-2xl font-extrabold text-[#1e3a5f] mb-2">You&apos;re in!</h2>
          {user ? (
            <p className="text-slate-500 mb-6" data-testid="success-message-registered">
              {success.tickets.length} ticket(s) sent to {buyerEmail}. Also available in My Tickets.
            </p>
          ) : (
            <p className="text-slate-500 mb-6" data-testid="success-message-unregistered">
              {success.tickets.length} ticket(s) emailed to {buyerEmail}. Please check your inbox (and spam folder) — your PDF ticket has all the details, including cancellation instructions.
            </p>
          )}
          <div className="bg-slate-50 rounded-xl p-4 mb-4 text-left text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Order</span><span className="font-mono text-xs">{success.order_id?.slice(0, 8)}</span></div>
            {success.voucher_portion > 0 && <div className="flex justify-between"><span className="text-slate-500">Voucher</span><span>−€{success.voucher_portion.toFixed(2)}</span></div>}
            {success.voucher_discount > 0 && <div className="flex justify-between" data-testid="success-voucher-discount"><span className="text-slate-500">Voucher discount</span><span>−€{success.voucher_discount.toFixed(2)}</span></div>}
            {success.wallet_portion > 0 && <div className="flex justify-between"><span className="text-slate-500">Wallet</span><span>−€{success.wallet_portion.toFixed(2)}</span></div>}
            <div className="flex justify-between font-bold pt-1 border-t border-slate-200"><span>Total</span><span>€{success.amount_paid.toFixed(2)}</span></div>
          </div>
          {user && <button onClick={() => navigate(`/${tenantSlug}/my-tickets`)} className="w-full py-3 rounded-full bg-[#1e3a5f] text-white font-bold mb-2" data-testid="view-my-tickets">View My Tickets</button>}
          <button onClick={() => navigate(`/${tenantSlug}`)} className="w-full py-3 rounded-full bg-white text-[#1e3a5f] border border-slate-200 font-medium">Back to Shop</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="checkout-page">
      {stalePreview && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="stale-cart-overlay">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-lg">
            <h3 className="font-bold text-lg text-[#1e3a5f] mb-2">Please review your cart</h3>
            <p className="text-sm text-slate-600 mb-4">{stalePreview.message}</p>
            {stalePreview.preview && (
              <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
                <p className="font-semibold text-slate-700 mb-2">Updated total</p>
                <p className="text-2xl font-extrabold text-[#0ea5e9]">€{Number(stalePreview.preview.total || 0).toFixed(2)}</p>
              </div>
            )}
            <button
              onClick={() => setStalePreview(null)}
              className="w-full bg-[#0ea5e9] text-white font-semibold rounded-lg py-2.5 hover:bg-[#0284c7]"
              data-testid="stale-cart-review-btn"
            >Review cart</button>
          </div>
        </div>
      )}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/${tenantSlug}`)} className="p-2 hover:bg-slate-100 rounded-full" data-testid="checkout-back-btn" aria-label="Back to shop"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
          <h1 className="font-bold text-[#1e3a5f]">Checkout</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Cart summary — grouped by event */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-bold text-[#1e3a5f] mb-3">Order Summary</h3>
          {isGuestOtp && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3" data-testid="guest-base-price-notice">
              You&apos;re checking out as an <span className="font-semibold">unregistered guest</span> — full base price applies (no discount). Register or log in for member/registered-guest pricing.
            </p>
          )}
          {upsellCopy && (
            <div
              className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3 flex items-start gap-2"
              data-testid={`upsell-savings-banner-${upsellTier}`}
            >
              <TrendingUp className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-emerald-800 font-semibold" data-testid="upsell-savings-title">
                  {upsellCopy.title}
                </p>
                <p className="text-emerald-700/90 mt-0.5">{upsellCopy.body}</p>
                <p className="text-[10px] text-emerald-700/70 italic mt-0.5">
                  Representative figure — actual savings depend on the tickets, packages, and member type.
                </p>
              </div>
            </div>
          )}
          {unavailableLines.length > 0 && (
            <div className="mb-3 space-y-1.5" data-testid="checkout-unavailable-list">
              {unavailableLines.map(l => (
                <UnavailableAlert key={l.id} reason={`${l.name}: ${unavailable[l.id]}`} testid={`checkout-unavailable-${l.id}`} onRemove={l.remove} />
              ))}
            </div>
          )}
          <div className="space-y-3">
            {preview && preview.lines.length > 0 ? (
              preview.lines.map((ln, idx) => (
                <div key={`l-${idx}`} className="text-sm border-b border-slate-100 last:border-b-0 pb-2 last:pb-0" data-testid={`preview-line-${idx}`}>
                  <div className="flex justify-between items-start">
                    <p className={`font-medium ${ln.is_member_price ? "text-[#2d8a4e]" : "text-[#1e3a5f]"}`}>{ln.label}</p>
                    <span className="font-bold">€{ln.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="mt-1 pl-1 space-y-0.5">
                    <div className="flex justify-between text-xs text-slate-500" data-testid={`preview-base-${idx}`}>
                      <span>{ln.quantity} × Base price €{(ln.base_unit_price ?? ln.unit_price).toFixed(2)}</span>
                      <span>€{((ln.base_unit_price ?? ln.unit_price) * ln.quantity).toFixed(2)}</span>
                    </div>
                    {ln.discount_amount > 0 && (
                      <div className="flex justify-between text-xs text-[#2d8a4e]" data-testid={`preview-discount-${idx}`}>
                        <span>− {ln.discount_label || "Discount"} (€{ln.discount_amount.toFixed(2)} × {ln.quantity})</span>
                        <span>−€{(ln.discount_amount * ln.quantity).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs text-slate-700 font-medium">
                      <span>Final €{ln.unit_price.toFixed(2)} × {ln.quantity}</span>
                      <span>€{ln.subtotal.toFixed(2)}</span>
                    </div>
                    {/* iter155: per-included-ticket breakdown for packages. Shows
                        each ticket's attributed member type, base and final so the
                        buyer sees exactly how €16.99 + €7.99 → €24.98 pkg base. */}
                    {Array.isArray(ln.sub_lines) && ln.sub_lines.length > 0 && (
                      <div className="mt-2 pl-3 border-l-2 border-slate-200 space-y-1" data-testid={`preview-sublines-${idx}`}>
                        {ln.sub_lines.map((sl, si) => (
                          <div key={`sl-${idx}-${si}`} className="text-[11px]" data-testid={`preview-subline-${idx}-${si}`}>
                            <div className="flex justify-between text-slate-600">
                              <span className="truncate mr-2">↳ {sl.label}</span>
                              <span className="font-medium">€{Number(sl.unit_price).toFixed(2)}</span>
                            </div>
                            {sl.discount_amount > 0 && (
                              <div className="flex justify-between text-[10px] text-[#2d8a4e]">
                                <span>&nbsp;&nbsp;&nbsp;Base €{Number(sl.base_price).toFixed(2)} − discount</span>
                                <span>−€{Number(sl.discount_amount).toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              // Fallback: show cart items at base prices (no server preview yet — e.g. anonymous, not verified).
              groupedByEvent.map(g => (
                <div key={g.event_id} className="space-y-2 border-b border-slate-100 last:border-b-0 pb-2 last:pb-0">
                  <p className="text-[10px] uppercase tracking-wide font-bold text-slate-400">{g.event_name}</p>
                  {g.items.map(i => (
                    <div key={i.id} className="flex justify-between items-center text-sm">
                      <div>
                        <p className="font-medium text-[#1e3a5f]">{i.ticket_type_name}{i.variant_name ? ` · ${i.variant_name}` : ""}</p>
                        <p className="text-xs text-slate-400">{i.quantity} × €{Number(i.base_unit_price || 0).toFixed(2)}</p>
                      </div>
                      <span className="font-bold">€{(Number(i.base_unit_price || 0) * i.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  {g.packages.map(p => (
                    <div key={p.id} className="flex justify-between items-center text-sm">
                      <div>
                        <p className="font-medium text-[#0ea5e9]">📦 {p.package_name}</p>
                        <p className="text-xs text-slate-400">{p.quantity} × €{Number(p.base_unit_price || 0).toFixed(2)}</p>
                      </div>
                      <span className="font-bold">€{(Number(p.base_unit_price || 0) * p.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
          <hr className="my-3 border-slate-200" />
          <div className="flex justify-between font-extrabold text-lg text-[#1e3a5f]">
            <span>Total</span><span data-testid="checkout-total">€{total.toFixed(2)}</span>
          </div>
        </div>

        {/* Voucher — available to everyone (validated anonymously via /voucher/preview
            when not logged in, and re-validated at final /checkout). */}
        {cartCount > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="font-bold text-[#1e3a5f] mb-3 flex items-center gap-2"><Tag className="w-4 h-4" /> Voucher</h3>
            {voucher ? (
              <div className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-xl">
                <div>
                  <p className="font-mono font-bold text-[#2d8a4e]" data-testid="voucher-applied-code">{voucher.code}</p>
                  <p className="text-[10px] text-slate-500" data-testid="voucher-applied-summary">{voucherSummary(voucher)}{voucher.requires_email_match ? " · verified at pay" : ""}</p>
                </div>
                <button onClick={() => { setVoucher(null); setVoucherCode(""); }} className="text-xs text-red-500" data-testid="remove-voucher">Remove</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input value={voucherCode} onChange={e => setVoucherCode(e.target.value.toUpperCase())} placeholder="Enter voucher code" className="flex-1 px-3 py-2 rounded-xl border text-sm uppercase" data-testid="voucher-code-input" />
                <button onClick={applyVoucher} disabled={!voucherCode || voucherLoading} className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-sm font-medium disabled:opacity-50" data-testid="apply-voucher-btn">
                  {voucherLoading ? "..." : "Apply"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Payment — always shown so non-logged shoppers see the same layout as logged in
            users (breakdown + you-pay). Wallet toggle only for real user accounts. */}
        {cartCount > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="font-bold text-[#1e3a5f] mb-3 flex items-center gap-2"><Wallet className="w-4 h-4" /> Payment</h3>

            {!isGuestOtp && user && (
              <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer mb-3" data-testid="wallet-toggle-label">
                <input
                  type="checkbox"
                  checked={useWallet}
                  onChange={e => setUseWallet(e.target.checked)}
                  disabled={walletBal <= 0}
                  className="mt-1 accent-[#1e3a5f]"
                  data-testid="use-wallet-checkbox"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#1e3a5f]">Pay with wallet balance</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Available: <span className="font-mono font-bold text-[#2d8a4e]">€{walletBal.toFixed(2)}</span>
                    {walletBal <= 0 && " (empty)"}
                  </p>
                </div>
              </label>
            )}

            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Cart total</span><span className="font-medium">€{total.toFixed(2)}</span></div>
              {voucherPortion > 0 && <div className="flex justify-between text-[#2d8a4e]" data-testid="voucher-line"><span>{voucher?.is_discount ? "Voucher discount" : "Voucher applied"}</span><span className="font-bold">−€{voucherPortion.toFixed(2)}</span></div>}
              {walletPortion > 0 && <div className="flex justify-between text-[#2d8a4e]" data-testid="wallet-line"><span>From wallet</span><span className="font-bold">−€{walletPortion.toFixed(2)}</span></div>}
              {stripeFee > 0 && (
                <>
                  <div className="flex justify-between text-slate-500" data-testid="processing-fee-line">
                    <span>Processing fee ({chargesLabel})</span>
                    <span>+€{stripeFee.toFixed(2)}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 flex items-center gap-1" data-testid="checkout-fee-nonrefundable-note">
                    <Info className="w-3 h-3" /> Note: Processing fees are non-refundable
                  </p>
                </>
              )}
              <div className="flex justify-between pt-2 border-t border-slate-100 text-lg font-extrabold text-[#1e3a5f]">
                <span>You pay now</span>
                <span data-testid="you-pay-amount">€{youPay.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Auth gate — shown LAST for non-logged shoppers so the layout mirrors
            the logged-in flow (Order Summary → Voucher → Payment → …). */}
        {!canCheckout && (
          <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4" data-testid="checkout-auth-gate">
            <div>
              <h3 className="font-bold text-[#1e3a5f] mb-1">Sign in for the best offers</h3>
              <p className="text-xs text-slate-500 mb-3">Registered users are eligible for special offers and discounts.</p>
              <button
                onClick={_redirectToLogin}
                className="w-full py-3 rounded-full bg-[#1e3a5f] text-white font-bold flex items-center justify-center gap-2"
                data-testid="checkout-login-btn"
              >
                <LogIn className="w-4 h-4" /> Login to continue
              </button>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <div className="flex-1 h-px bg-slate-200" /> or <div className="flex-1 h-px bg-slate-200" />
            </div>
            <div>
              {guestStage === "existing_user" ? (
                <>
                  <h3 className="font-bold text-[#1e3a5f] mb-1">Welcome back</h3>
                  <p className="text-xs text-slate-500 mb-3">
                    Enter your password to unlock member/guest offers and continue with the tickets in your cart.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="font-bold text-[#1e3a5f] mb-1">Continue as an unregistered guest</h3>
                  <p className="text-xs text-slate-500 mb-3">
                    We&apos;ll email you your tickets. <span className="font-semibold">Base price applies — no member/registered-guest discounts.</span>
                  </p>
                </>
              )}

              {guestStage === "start" && (
                <div className="space-y-2" data-testid="guest-checkout-form">
                  <div className="relative">
                    <UserIcon className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    <input
                      value={guestName}
                      onChange={e => setGuestName(e.target.value)}
                      placeholder="Your name"
                      className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm"
                      data-testid="guest-name-input"
                    />
                  </div>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={e => setGuestEmail(e.target.value.trim())}
                      placeholder="you@example.com"
                      className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm"
                      data-testid="guest-email-input"
                    />
                  </div>
                  <button
                    onClick={sendGuestOtp}
                    disabled={guestSending}
                    className="w-full py-3 rounded-full bg-[#2d8a4e] text-white font-bold disabled:opacity-50"
                    data-testid="guest-send-otp-btn"
                  >
                    {guestSending ? "Sending..." : "Send verification code"}
                  </button>
                </div>
              )}

              {guestStage === "existing_user" && (
                <div className="space-y-2" data-testid="guest-existing-user-form">
                  <p className="text-xs text-slate-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    <span className="font-semibold">{guestEmail}</span> is already registered. {guestExistingMessage || "Please log in with your password."}
                  </p>
                  <div className="relative">
                    <KeyRound className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    <input
                      type="password"
                      value={guestPassword}
                      onChange={e => setGuestPassword(e.target.value)}
                      placeholder="Your password"
                      className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm"
                      data-testid="guest-existing-password-input"
                      onKeyDown={e => { if (e.key === "Enter") loginExistingUser(); }}
                    />
                  </div>
                  <button
                    onClick={loginExistingUser}
                    disabled={guestVerifying}
                    className="w-full py-3 rounded-full bg-[#1e3a5f] text-white font-bold disabled:opacity-50"
                    data-testid="guest-existing-login-btn"
                  >
                    {guestVerifying ? "Signing in..." : "Log in & continue"}
                  </button>
                  <div className="flex items-center justify-between text-xs">
                    <button
                      onClick={() => { setGuestStage("start"); setGuestPassword(""); setGuestExistingMessage(""); }}
                      className="text-slate-500 hover:underline"
                      data-testid="guest-existing-back-btn"
                    >Use a different email</button>
                    <button
                      onClick={() => navigate(`/${tenantSlug}/forgot-password?email=${encodeURIComponent(guestEmail)}`)}
                      className="text-[#1e3a5f] hover:underline"
                      data-testid="guest-existing-forgot-btn"
                    >Forgot password?</button>
                  </div>
                </div>
              )}

              {guestStage === "otp_sent" && (
                <div className="space-y-2" data-testid="guest-otp-form">
                  <p className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2">
                    Code sent to <span className="font-semibold">{guestEmail}</span>. Enter it below (expires in 15 minutes).
                  </p>
                  <div className="relative">
                    <KeyRound className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    <input
                      value={guestOtp}
                      onChange={e => setGuestOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="6-digit code"
                      inputMode="numeric"
                      className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm font-mono tracking-widest"
                      data-testid="guest-otp-input"
                    />
                  </div>
                  <button
                    onClick={verifyGuestOtp}
                    disabled={guestVerifying}
                    className="w-full py-3 rounded-full bg-[#2d8a4e] text-white font-bold disabled:opacity-50"
                    data-testid="guest-verify-otp-btn"
                  >
                    {guestVerifying ? "Verifying..." : "Verify & continue"}
                  </button>
                  <button
                    onClick={() => { setGuestStage("start"); setGuestOtp(""); }}
                    className="w-full text-xs text-slate-500 hover:underline"
                    data-testid="guest-otp-back-btn"
                  >
                    Change email
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Voucher — DUPLICATE REMOVED (moved earlier in the layout) */}

        {/* Payment — DUPLICATE REMOVED */}

        {canCheckout && (
          <button
            onClick={handlePay}
            disabled={processing || cartCount === 0 || unavailableLines.length > 0}
            className="w-full py-4 rounded-full bg-[#2d8a4e] text-white font-bold shadow-lg disabled:opacity-50"
            data-testid="pay-now-btn"
          >
            {processing
              ? "Processing..."
              : youPay === 0
                ? `Confirm — Fully covered (€${total.toFixed(2)})`
                : `Pay €${youPay.toFixed(2)}`}
          </button>
        )}
      </main>
    </div>
  );
}
